import { useCallback, useMemo, useState } from 'react'
import { useGalleryAssignment, useGalleryDeal, useGalleryProduct, useGalleryVisualSelection, useGalleryVisualsMeta } from '../../../contexts'
import { useLocation, useNavigate, useParams } from 'react-router'

import { AnalyticsEvent } from 'utils/analytics'
import { AssignmentDTOIsClientDTO } from 'utils/typeguards'
import { AssignmentStage } from 'constants/assignment'
import { PRODUCTS_WITH_OPTIONAL_STAGING } from 'constants/product/stagingProductKinds'
import { Path } from 'constants/router'
import { choosePrimaryProduct } from 'utils/product'
import constate from 'constate'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { useGetAssignmentDocumentsStatusHistory } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

export enum GalleryFilter {
  ALL = 'ALL',
  FAVORITES = 'FAVORITES',
  PURCHASED = 'PURCHASED',
  AI_ENHANCED = 'AI_ENHANCED',
}

export enum CurrentActionType {
  DOWNLOAD = 'DOWNLOAD',
  EDITING = 'EDITING',
}

export enum ToolbarMode {
  SELECTION = 'SELECTION',
  EXPORT_DOWNLOAD = 'EXPORT_DOWNLOAD',
  UPLOAD_VISUALS = 'UPLOAD_VISUALS',
}

export const [ClientGalleryContextProvider, useClientGallery] = constate(({
  assignmentId,
}: {
  assignmentId: string
}) => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const { pathname } = useLocation()

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  const { userPropertyWorkspaceId } = useUserData()
  const { stagingAssignments, dealAssignmentsForTab } = useGalleryDeal()
  const { selectedNotPurchasedVisualsCount, selectedPurchasedVisualsCount } = useGalleryVisualSelection()
  const { purchasedVisualsExist } = useGalleryVisualsMeta()
  const { isAuthoritiesDocumentsProduct } = useGalleryProduct()
  const {
    logGalleryEvent,
    product,
    assignmentData,
    isDocSendToClient
  } = useGalleryAssignment()

  // Gallery Filter selection - All/Purchased
  const [filter, setFilterRow] = useState(GalleryFilter.ALL)
  const [isToolBarOpen, setIsToolBarOpen] = useState<boolean>(false)
  const [currentActionType, setCurrentActionType] = useState(CurrentActionType.DOWNLOAD)
  const [isUploadPopupOpened, setIsUploadPopupOpened] = useState(false)

  const setFilter = useCallback((filter: GalleryFilter) => {
    setFilterRow(filter)

    logGalleryEvent(AnalyticsEvent.USE_GALLERY_FILTER, { filter })
  }, [logGalleryEvent])

  // Enabling the client additional staging flow for Gallery
  // Checking if there are deal assignments with additional staging.
  // Checking if any staging assignments are not at the 'VISUALS_SENT_TO_CLIENT' stage.
  // Verifying if the product kind is part of the products that have optional staging.
  const enableStagingFlowGallery = useMemo(
    () => {
      return !!stagingAssignments.length
        && !!product?.kind
        && dealAssignmentsForTab.some((assignment) => {
          const primaryProduct = choosePrimaryProduct(assignment.products)
          return !!primaryProduct && PRODUCTS_WITH_OPTIONAL_STAGING.has(primaryProduct.kind)
        })
        && stagingAssignments.some((assignment) => assignment.stage !== AssignmentStage.VISUALS_SENT_TO_CLIENT)
        && PRODUCTS_WITH_OPTIONAL_STAGING.has(product.kind)
    },
    [product?.kind, stagingAssignments, dealAssignmentsForTab]
  )

  // Navigate to staging flow => current assignment will be base for selection of images (will break if navigated from non-photo assignment)
  const openStagingFlow = () => {

    if (isPropertyPathname) {
      if (id && assignmentId) {
        navigate(Path.PROPERTY_ASSIGNMENT_STAGING
          .replace(':id', id.toString())
          .replace(':assignmentId', assignmentId.toString()),
          { state: { stagingAssignments } })
      }
    } else {
      navigate(Path.GALLERY_STAGING.replace(':id', assignmentId.toString()))
    }

  }

  const mode = useMemo(() => {
    if (!purchasedVisualsExist && selectedNotPurchasedVisualsCount > 0) return ToolbarMode.SELECTION
    if (selectedNotPurchasedVisualsCount > 0) return ToolbarMode.SELECTION
    if (selectedPurchasedVisualsCount > 0) return ToolbarMode.EXPORT_DOWNLOAD
    if (isPropertyPathname) return ToolbarMode.UPLOAD_VISUALS
    if (!purchasedVisualsExist) return

    return ToolbarMode.EXPORT_DOWNLOAD
  }, [isPropertyPathname, purchasedVisualsExist, selectedNotPurchasedVisualsCount, selectedPurchasedVisualsCount])

  const assignmentDocumentsStatusHistory = useGetAssignmentDocumentsStatusHistory(isAuthoritiesDocumentsProduct ? assignmentId : '', userPropertyWorkspaceId)
  const assignmentDocumentsStatusHistoryData = useMemo(() => assignmentDocumentsStatusHistory.data?.data || [], [assignmentDocumentsStatusHistory.data?.data])
  const assignmentDocumentsEstimatedDelivery = useMemo(() => {
    if (assignmentData && AssignmentDTOIsClientDTO(assignmentData) && !isDocSendToClient) {
      return assignmentData.expectedResponseDate
    }

    return undefined
  }, [assignmentData, isDocSendToClient])

  const showDocumentStatusHistory = useMemo(() => {
    return assignmentDocumentsStatusHistoryData.length > 0 || !!assignmentDocumentsEstimatedDelivery
  }, [assignmentDocumentsStatusHistoryData.length, assignmentDocumentsEstimatedDelivery])

  return {
    mode,
    assignmentId,
    filter,
    setFilter,
    isToolBarOpen,
    setIsToolBarOpen,
    currentActionType,
    setCurrentActionType,
    enableStagingFlowGallery,
    openStagingFlow,
    isUploadPopupOpened,
    setIsUploadPopupOpened,
    assignmentDocumentsStatusHistoryData,
    assignmentDocumentsEstimatedDelivery,
    showDocumentStatusHistory,
    isPropertyPathname
  }
})
